import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { apiErr } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Input, Label } from "../../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Pencil, Sparkles, Check, X, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { PageShell, Stat } from "./PageShell";

const MEASURE_LABEL = { area: "Area", energy: "Energy", volume: "Volume", mass: "Mass", weight: "Weight" };

export default function PlatformLocaleMaster() {
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState([]);
  const [dims, setDims] = useState({ measures: {} });
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [aiCountry, setAiCountry] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/locale/admin/master").then((r) => setItems(r.data.items || [])).catch((e) => toast.error(apiErr(e)));
    api.get("/locale/admin/pending").then((r) => setPending(r.data.items || [])).catch(() => {});
    api.get("/locale/dimensions").then((r) => setDims(r.data || { measures: {} })).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const measures = Object.keys(dims.measures || {});
  const published = items.filter((i) => i.status === "published");

  const openNew = () => {
    const units = {};
    measures.forEach((m) => { units[m] = (dims.measures[m]?.units || [])[0]; });
    setForm({ country: "", units, currency: "USD", isNew: true });
    setEditOpen(true);
  };
  const openEdit = (loc) => {
    setForm({ country: loc.country, units: { ...loc.units }, currency: loc.currency, isNew: false });
    setEditOpen(true);
  };
  const closeEdit = () => { setEditOpen(false); setForm(null); };
  const setUnit = (m, u) => setForm((p) => ({ ...p, units: { ...p.units, [m]: u } }));

  const save = async () => {
    if (!form.country.trim()) { toast.error("Country is required"); return; }
    try {
      await api.post("/locale/admin/upsert", {
        country: form.country.trim(), units: form.units, currency: form.currency.trim().toUpperCase(),
      });
      toast.success(`Locale published for ${form.country.trim()}`); closeEdit(); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const aiPropose = async () => {
    if (!aiCountry.trim()) return;
    setAiBusy(true);
    try {
      await api.post("/locale/admin/ai-propose", { country: aiCountry.trim() });
      toast.success(`Proposal queued for ${aiCountry.trim()} — review below`); setAiCountry(""); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setAiBusy(false); }
  };

  const publishPending = async (id, country) => {
    try { await api.post(`/locale/admin/${id}/publish`); toast.success(`Published locale for ${country}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const rejectPending = async (id) => {
    try { await api.delete(`/locale/admin/${id}`); toast.success("Proposal rejected"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <PageShell eyebrow="Platform Operations" title="Country Locale Master"
      subtitle="Curate each country's customary reporting units (area, energy, volume, mass, weight) and currency. Publishing supersedes the old version (kept for history). AI can propose locales for new countries for your review."
      testid="platform-locale-master"
      actions={<Button variant="primary" onClick={openNew} data-testid="add-locale-btn"><Plus className="h-3.5 w-3.5" /> Add / update country</Button>}>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Published countries" value={published.length} tone="accent" />
        <Stat label="Pending review" value={pending.length} tone="amber" />
        <Stat label="Total versions" value={items.length} />
        <Stat label="Measures" value={measures.length} />
      </div>

      <div className="card-flat p-4 flex flex-wrap items-end gap-3" data-testid="locale-ai-propose">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs">Propose a locale for a new country (AI)</Label>
          <Input value={aiCountry} onChange={(e) => setAiCountry(e.target.value)} placeholder="e.g. Brazil"
            data-testid="ai-country-input" onKeyDown={(e) => e.key === "Enter" && aiPropose()} />
        </div>
        <Button variant="outline" onClick={aiPropose} disabled={aiBusy || !aiCountry.trim()} data-testid="ai-propose-btn">
          <Sparkles className="h-3.5 w-3.5" /> {aiBusy ? "Proposing…" : "AI propose"}
        </Button>
      </div>

      {pending.length > 0 && (
        <div className="card-flat p-4" data-testid="locale-pending">
          <div className="label-eyebrow mb-2">Pending proposals — review & publish</div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Country</TableHead><TableHead>Currency</TableHead>
                {measures.map((m) => <TableHead key={m}>{MEASURE_LABEL[m]}</TableHead>)}
                <TableHead>Source</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {pending.map((p) => (
                  <TableRow key={p.id} data-testid={`locale-pending-${p.country}`}>
                    <TableCell className="font-medium text-sm">{p.country}</TableCell>
                    <TableCell className="font-mono text-xs">{p.currency}</TableCell>
                    {measures.map((m) => <TableCell key={m} className="text-xs">{p.units?.[m]}</TableCell>)}
                    <TableCell><Badge variant="outline" className="text-[9px]">{p.source}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Publish" onClick={() => publishPending(p.id, p.country)} data-testid={`locale-publish-${p.country}`}><Check className="h-3.5 w-3.5 text-[#00E65B]" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Reject" onClick={() => rejectPending(p.id)} data-testid={`locale-reject-${p.country}`}><X className="h-3.5 w-3.5 text-red-400" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <div className="card-flat overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Country</TableHead><TableHead>Currency</TableHead>
              {measures.map((m) => <TableHead key={m}>{MEASURE_LABEL[m]}</TableHead>)}
              <TableHead>Version</TableHead><TableHead>Source</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {published.length === 0 && <TableRow><TableCell colSpan={measures.length + 5} className="text-center text-muted-foreground py-10">No published locales yet.</TableCell></TableRow>}
            {published.map((loc) => (
              <TableRow key={loc.id} data-testid={`locale-row-${loc.country}`}>
                <TableCell className="font-medium text-sm"><span className="inline-flex items-center gap-1.5"><Globe2 className="h-3.5 w-3.5 text-accent" /> {loc.country}</span></TableCell>
                <TableCell className="font-mono text-xs">{loc.currency}</TableCell>
                {measures.map((m) => <TableCell key={m} className="text-xs">{loc.units?.[m]}</TableCell>)}
                <TableCell className="text-[11px] text-muted-foreground">v{loc.version}{loc.effective_date ? ` · ${loc.effective_date}` : ""}</TableCell>
                <TableCell><Badge variant={loc.source === "seed" ? "outline" : loc.source === "curated" ? "success" : "lime"} className="text-[9px]">{loc.source}</Badge></TableCell>
                <TableCell className="text-right"><Button variant="ghost" size="icon" className="h-7 w-7" title="Edit / new version" onClick={() => openEdit(loc)} data-testid={`locale-edit-${loc.country}`}><Pencil className="h-3.5 w-3.5 text-muted-foreground" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={editOpen} onOpenChange={(o) => { if (!o) closeEdit(); }}>
        <DialogContent className="max-w-lg" data-testid="locale-edit-dialog">
          <DialogHeader><DialogTitle className="font-display">{form?.isNew ? "Add / update country locale" : `Edit ${form?.country}`}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Country *</Label><Input value={form.country} disabled={!form.isNew} onChange={(e) => setForm((p) => ({ ...p, country: e.target.value }))} placeholder="Singapore" data-testid="locale-country-input" /></div>
                <div><Label className="text-xs">Currency (ISO) *</Label><Input value={form.currency} onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value }))} placeholder="SGD" data-testid="locale-currency-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {measures.map((m) => (
                  <div key={m}><Label className="text-xs">{MEASURE_LABEL[m]}</Label>
                    <Select value={form.units[m]} onValueChange={(v) => setUnit(m, v)}>
                      <SelectTrigger data-testid={`locale-unit-${m}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{(dims.measures[m]?.units || []).map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">Publishing creates a new version and supersedes the current published locale for this country. Existing records keep their stored values; only reporting re-display uses the latest units.</p>
              <div className="flex justify-end gap-2 pt-1"><Button variant="outline" onClick={closeEdit}>Cancel</Button><Button variant="primary" onClick={save} data-testid="locale-save-btn">Publish</Button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
